import { type ChangeEvent, forwardRef, type HTMLAttributes, type KeyboardEvent } from "react";

import { Box, type InputBaseComponentProps } from "@mui/material";

const zeroWidthSpace = "\u200B";

function ensureEnterKeyCreatesNewLine(onKeyDownEvent: KeyboardEvent<HTMLInputElement>) {
  if (onKeyDownEvent.key === "Enter") {
    onKeyDownEvent.preventDefault();
    onKeyDownEvent.stopPropagation();
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const br = document.createElement("br");
      const nextTextNode = document.createTextNode(zeroWidthSpace);

      range.deleteContents();
      range.insertNode(nextTextNode);
      range.insertNode(br);

      range.setStartBefore(nextTextNode);
      range.setEndAfter(nextTextNode);
      range.collapse(true);
    }
  }
}

type InputWithChipsProps = HTMLAttributes<HTMLDivElement> & {
  rows: number;
  maxRows: number;
  disabled?: boolean;
};

export const InputWithChips = forwardRef<HTMLDivElement, InputBaseComponentProps>((props, ref) => {
  const { disabled, rows, maxRows, onChange, onInput, ...rest } = props as InputWithChipsProps;
  return (
    <Box
      {...rest}
      contentEditable={!disabled}
      onInput={(event) => {
        onInput?.(event);

        // info: this is a workaround to be compliant with types and logic required by MUI OutlinedInput
        const castedEvent = event as unknown as ChangeEvent<HTMLInputElement>;
        castedEvent.target.value =
          event.currentTarget.innerText?.replace(/\s+/g, "") || event.currentTarget.children.length > 0 ? " " : "";

        onChange?.(castedEvent);
      }}
      onKeyDown={ensureEnterKeyCreatesNewLine}
      ref={ref}
      sx={{
        minHeight: `${rows * 1.4375}em`,
        maxHeight: `${maxRows * 1.4375}em`,
        height: "auto !important",
        overflowX: "hidden",
        overflowY: "auto",
        "& > span": {
          display: "inline-block",
          verticalAlign: "middle",
          maxWidth: "100%",
        },
      }}
    />
  );
});

InputWithChips.displayName = "InputWithChips";
